import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { RadioButtonOption } from '../components/RadioButton';
import { TextInput } from '../components/TextInput';
import { Flex } from 'antd';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  ModalFooter,
  SaveButton,
  CancelButton,
  DeleteButton,
  FormContainer,
  FormControl,
  FieldLabel,
  TextArea,
  DurationContainer,
  DurationInput,
  DurationLabel,
  StyledAutoComplete,
  StyledCheckbox,
  StyledRadioButton,
  DateTimeRow,
  DateTimeColumn,
  AddPlayerContainer,
  PlayerInputWrapper,
  PlayerNumberInput,
  PlayerList,
  PlayerItem,
  PlayerInfo,
  RemoveButton,
  SectionTitle,
  PlayerNumber,
  PlayerName,
  StyledDateInput,
  StyledTimeInput,
  DateTimeFieldGroup,
  RoleToggleButton,
  PlayerActions,
  PlayerFlagLabel,
  PlaysSection,
  PlaysSectionHeader,
  PlaysGrid,
  PlayCard,
  PlayCardName,
  PlayCardRemoveButton,
  PlaysEmptyState,
  PlayBrowserModalOverlay,
  PlayBrowserModalHeader,
  PlayBrowserModalTitle,
  PlayBrowserModalContent,
  TrainingTypeList,
  TrainingTypeItem,
  TrainingTypeName,
  TrainingTypeRemoveButton,
} from './PlanFormPage.style';
import { useStore } from '@nanostores/react';
import { Button as AppButton } from '../components/Button';
import {
  $competitionTypes,
  $plannerModalIsOpen,
  $players,
  $selectedDetailsEvent,
  $selectedEvent,
  $trainingTypes,
  Player,
  PlannerEvent,
  removeEvent,
  saveEvent,
} from '../store/planner';
import { XMarkIcon } from '@heroicons/react/24/outline';
import dayjs, { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { darkBlueBackground } from '../components/colors';
import { Play, Syncable } from '../types';
import { $plays } from '../store/plays';
import { positions } from '../positions';
import { PoolElement } from '../Pool';
import { PlayBrowser } from '../components/PlayBrowser';
import { PlayPlayer } from '../components/PlayPlayer';

export type EventType = 'match' | 'training' | 'other';

export const eventTypeOptions: RadioButtonOption[] = [
  { id: 'match', main: 'Match Playbook' },
  { id: 'training', main: 'Training' },
  { id: 'other', main: 'Note' },
];

export const PlanFormPage: FC = () => {
  // Store values
  const isOpen = useStore($plannerModalIsOpen);
  const selectedEvent = useStore($selectedEvent);
  const availablePlayers = useStore($players);
  const availableTrainingTypes = useStore($trainingTypes);
  const availableCompetitionTypes = useStore($competitionTypes);

  // Basic event fields
  const [eventTitle, setEventTitle] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, 'hour'));
  const [isAllDay, setIsAllDay] = useState(false);

  const [eventType, setEventType] = useState<EventType>('match');
  const [description, setDescription] = useState('');
  const [note, setNote] = useState<string>(''); // General note for all types

  // Training-specific fields
  const [trainingDuration, setTrainingDuration] = useState<number | null>(null);
  const [durationManuallySet, setDurationManuallySet] = useState(false);
  const [trainingType, setTrainingType] = useState<string>('');
  const [trainingTypes, setTrainingTypes] = useState<string[]>([]);

  // Match-specific fields
  const [competitionType, setCompetitionType] = useState<string>('');
  const [matchPlan, setMatchPlan] = useState<string>('');
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);

  // Plays state (PIP-1)
  const [plays, setPlays] = useState<Play[]>([]);

  // PlayBrowser modal state (PIP-3)
  const [isPlayBrowserOpen, setIsPlayBrowserOpen] = useState(false);

  // PlayPlayer preview state (PIP-4)
  const [playToPreview, setPlayToPreview] = useState<Play | null>(null);

  // Player input fields
  const [newPlayerName, setNewPlayerName] = useState<string>('');
  const [newPlayerNumber, setNewPlayerNumber] = useState<number | null>(null);
  const [newPlayerData, setNewPlayerData] = useState<Player | null>(null);

  // Auto-calculate training duration from start/end when not manually set
  const calcDurationMinutes = (
    start: Dayjs | null,
    end: Dayjs | null,
  ): number | null => {
    if (start && end) {
      const diff = end.diff(start, 'minute');
      return diff > 0 ? diff : null;
    }
    return null;
  };

  useEffect(() => {
    if (eventType === 'training' && !durationManuallySet) {
      setTrainingDuration(calcDurationMinutes(startDate, endDate));
    }
  }, [startDate, endDate, eventType, durationManuallySet]);

  const modalContentRef = useRef<HTMLDivElement>(null);
  // Load data from selectedEvent when modal opens
  useEffect(() => {
    if (isOpen && selectedEvent) {
      // Load basic event fields
      setEventTitle(selectedEvent.title?.toString() || '');
      setStartDate(
        selectedEvent.start ? dayjs(selectedEvent.start.toString()) : dayjs(),
      );
      setEndDate(
        selectedEvent.end
          ? dayjs(selectedEvent.end.toString())
          : dayjs().add(1, 'hour'),
      );
      setIsAllDay(selectedEvent.allDay || false);

      // Load common fields
      setEventType(selectedEvent.planType);
      setDescription(selectedEvent.description || '');
      setNote(selectedEvent.note || '');

      // Load training-specific fields
      if (selectedEvent.planType === 'training') {
        if (selectedEvent.trainingDuration != null) {
          setTrainingDuration(selectedEvent.trainingDuration);
          setDurationManuallySet(true);
        } else {
          // Calculate from start/end if no saved duration
          const start = selectedEvent.start
            ? dayjs(selectedEvent.start.toString())
            : null;
          const end = selectedEvent.end
            ? dayjs(selectedEvent.end.toString())
            : null;
          setTrainingDuration(calcDurationMinutes(start, end));
          setDurationManuallySet(false);
        }
        setTrainingTypes(selectedEvent.trainingTypes || []);
        setTrainingType('');
      }

      // Load match-specific fields
      if (selectedEvent.planType === 'match') {
        setCompetitionType(selectedEvent.competitionType || '');
        setMatchPlan(selectedEvent.matchPlan || '');
        setSelectedPlayers(selectedEvent.players || []);
      }

      if (
        selectedEvent.planType === 'match' ||
        selectedEvent.planType === 'training'
      ) {
        setPlays(selectedEvent.plays || []);
      }

      setTimeout(() => {
        modalContentRef.current?.scrollTo(0, 0);
      }, 100);
    } else if (isOpen && !selectedEvent) {
      // Reset form for new event
      resetForm();
    }
  }, [isOpen, selectedEvent]);

  const resetForm = () => {
    setEventTitle('');
    setStartDate(dayjs());
    setEndDate(dayjs().add(1, 'hour'));
    setIsAllDay(false);
    setEventType('match');
    setDescription('');
    setNote('');
    setTrainingDuration(null);
    setDurationManuallySet(false);
    setTrainingType('');
    setTrainingTypes([]);
    setCompetitionType('');
    setMatchPlan('');
    setSelectedPlayers([]);
    setPlays([]);
    setNewPlayerName('');
    setNewPlayerNumber(null);
  };

  const handleClose = () => {
    $plannerModalIsOpen.set(false);
    $selectedEvent.set(null);
  };

  const buildEvent = (): Syncable<PlannerEvent> | null => {
    if (eventTitle === '' || eventTitle === null) {
      return null;
    }

    console.log('Saving event:', {
      eventTitle,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      isAllDay,
      eventType,
      description,
      note,
      trainingDuration,
      trainingTypes,
      competitionType,
      matchPlan,
      selectedPlayers,
    });

    let color =
      eventType === 'match'
        ? '#FF5733'
        : eventType === 'training'
          ? '#12cc2e'
          : '#b8b510';

    const isNew = !selectedEvent?.id;

    const savedEvent: Syncable<PlannerEvent> = {
      id: selectedEvent?.id ?? uuidv4(),
      title: eventTitle,
      planType: eventType,
      start: startDate?.toISOString(),
      end: endDate?.toISOString(),
      allDay: isAllDay,
      description: description,
      note: note,
      matchPlan: matchPlan,
      trainingDuration: trainingDuration ?? undefined,
      trainingTypes: trainingTypes,
      competitionType: competitionType,
      color: color,
      players: selectedPlayers,
      plays:
        eventType === 'match' || eventType === 'training' ? plays : undefined,
      isDraft: isNew ? true : selectedEvent?.isDraft,
      synced: isNew ? undefined : selectedEvent?.synced,
    };

    return savedEvent;
  };

  const handleSave = async () => {
    const event = buildEvent();
    if (event) {
      await saveEvent(event);
      handleClose();
    }
  };

  const handleSaveAndView = async () => {
    const event = buildEvent();
    if (event) {
      await saveEvent(event);
      handleClose();
      setTimeout(() => {
        $selectedDetailsEvent.set(event);
      }, 200);
    }
  };

  const handleDelete = async () => {
    if (selectedEvent && selectedEvent.id) {
      await removeEvent(selectedEvent.id as string);
      handleClose();
    }
  };

  // Predefined training types with autocomplete
  const trainingTypeOptions = [
    { value: 'Endurance Training' },
    { value: 'Tactical Training' },
    { value: 'Technical Skills' },
    { value: 'Strength & Conditioning' },
    { value: 'Team Strategy' },
    { value: 'Shooting Practice' },
    { value: 'Defense Drills' },
    { value: 'Game Simulation' },
    { value: 'Recovery Session' },
  ];

  const mergedTrainingTypeOptions = useMemo(() => {
    const hardcoded = trainingTypeOptions.map((o) => o.value);
    const userDefined = availableTrainingTypes.map((t) => t.name);
    const all = [...new Set([...hardcoded, ...userDefined])];
    return all
      .filter((name) => !trainingTypes.includes(name))
      .map((name) => ({ value: name }));
  }, [availableTrainingTypes, trainingTypes]);

  // Predefined competition types
  const competitionTypeOptions = [
    { value: 'Liga' },
    { value: 'Cup' },
    { value: 'Championship' },
    { value: 'Friendly Match' },
    { value: 'Tournament' },
    { value: 'Playoff' },
    { value: 'Qualification' },
  ];

  const mergedCompetitionTypeOptions = useMemo(() => {
    const hardcoded = competitionTypeOptions.map((o) => o.value);
    const userDefined = availableCompetitionTypes.map((t) => t.name);
    const all = [...new Set([...hardcoded, ...userDefined])];
    return all.map((name) => ({ value: name }));
  }, [availableCompetitionTypes]);

  // Player autocomplete options from store
  const playerNameOptions = useMemo(() => {
    const rawPlayers = availablePlayers.map((player) => ({
      value: player.name,
      label: (
        <Flex
          gap={10}
          align={'center'}
          style={{ borderBottom: '1px solid #eee', padding: '4px 0' }}
        >
          <Flex
            style={{
              width: '25px',
              height: '25px',
              textAlign: 'center',
              borderRadius: '25px',
              background: darkBlueBackground,
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '14px',
            }}
            align={'center'}
            justify={'center'}
          >
            {player.number}
          </Flex>
          <div style={{ flex: 1 }}>
            <div>
              {player.name} (#${player.number})
            </div>
            <div>{player.flag}</div>
          </div>
        </Flex>
      ),
      player: player,
    }));

    // Remove if selectedPlayers already contains the player
    return rawPlayers.filter(
      (option) => !selectedPlayers.some((p) => p.id === option.player.id),
    );
  }, [availablePlayers, selectedPlayers]);

  // Plays store for PlayBrowser (PIP-3)
  const playsStore = useStore($plays);
  const folders = ['6 - 6', '6 - 5', 'Custom'];

  const calculatedPlays = useMemo(() => {
    return folders.reduce<Record<string, Play[]>>((result, folder) => {
      return {
        ...result,
        [folder]: [...playsStore, ...positions].filter(
          (play) => play.category === folder,
        ),
      };
    }, {});
  }, [playsStore]);

  const handleAddTrainingType = () => {
    if (trainingType.trim() && !trainingTypes.includes(trainingType.trim())) {
      setTrainingTypes([...trainingTypes, trainingType.trim()]);
      setTrainingType('');
    }
  };

  const handleRemoveTrainingType = (typeToRemove: string) => {
    setTrainingTypes(trainingTypes.filter((t) => t !== typeToRemove));
  };

  const handleDescriptionChange = (value: string) => {
    if (value.length <= 120) {
      setDescription(value);
    }
  };

  const handleAddPlayer = () => {
    // check is added
    const found = selectedPlayers.some(
      (p) => p.name === newPlayerName && p.number === newPlayerNumber,
    );
    if (found) {
      setNewPlayerName('');
      setNewPlayerNumber(null);
      setNewPlayerData(null);
      return false;
    }
    if (newPlayerName && newPlayerNumber !== null) {
      const newPlayer: Player = {
        id: newPlayerData?.id ?? uuidv4(),
        name: newPlayerName,
        number: newPlayerNumber,
        flag: newPlayerData?.flag ?? '',
        role: 'str',
      };
      setSelectedPlayers([...selectedPlayers, newPlayer]);
      setNewPlayerName('');
      setNewPlayerNumber(null);
      setNewPlayerData(null);
    }
  };

  const handleRemovePlayer = (playerId: string) => {
    setSelectedPlayers(selectedPlayers.filter((p) => p.id !== playerId));
  };

  const handleTogglePlayerRole = (playerId: string) => {
    setSelectedPlayers(
      selectedPlayers.map((p) =>
        p.id === playerId
          ? { ...p, role: p.role === 'sub' ? 'str' : 'sub' }
          : p,
      ),
    );
  };

  const handlePlayerNameSelect = (value: string, option: any) => {
    if (option?.player) {
      // Pre-fill the number from the selected player
      setNewPlayerNumber(option.player.number);
      setNewPlayerData(option.player);
    } else {
      setNewPlayerData(null);
    }
    setNewPlayerName(value);
  };

  console.log('selectedPlayers', selectedPlayers);

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {selectedEvent ? 'Edit Event' : 'Create New Event'}
          </ModalTitle>
          <CloseButton onClick={handleClose}>
            <div>
              <XMarkIcon
                style={{
                  color: '#fff',
                  height: '30px',
                  width: '30px',
                }}
                fontSize={30}
              />
            </div>
          </CloseButton>
        </ModalHeader>

        <ModalContent ref={modalContentRef}>
          <FormContainer>
            <FormControl>
              <TextInput
                label="Event Title"
                placeholder="Enter event title..."
                value={eventTitle}
                onChange={setEventTitle}
                disableForceUppercase={true}
              />
            </FormControl>

            <DateTimeRow>
              <DateTimeColumn>
                <FieldLabel>Start Date</FieldLabel>
                <DateTimeFieldGroup>
                  <StyledDateInput
                    type="date"
                    value={startDate?.format('YYYY-MM-DD') || ''}
                    onChange={(e) => {
                      const newDateStr = e.target.value;
                      if (!newDateStr) {
                        setStartDate(null);
                        return;
                      }
                      const existingTime =
                        startDate?.format('HH:mm') || '00:00';
                      setStartDate(dayjs(newDateStr + ' ' + existingTime));
                    }}
                  />
                  {!isAllDay && (
                    <StyledTimeInput
                      type="time"
                      step="900"
                      value={startDate?.format('HH:mm') || ''}
                      onChange={(e) => {
                        const newTime = e.target.value;
                        if (!newTime || !startDate) return;
                        const existingDate = startDate.format('YYYY-MM-DD');
                        setStartDate(dayjs(existingDate + ' ' + newTime));
                      }}
                    />
                  )}
                </DateTimeFieldGroup>
              </DateTimeColumn>
              <DateTimeColumn>
                <FieldLabel>End Date</FieldLabel>
                <DateTimeFieldGroup>
                  <StyledDateInput
                    type="date"
                    value={endDate?.format('YYYY-MM-DD') || ''}
                    min={startDate?.format('YYYY-MM-DD') || ''}
                    onChange={(e) => {
                      const newDateStr = e.target.value;
                      if (!newDateStr) {
                        setEndDate(null);
                        return;
                      }
                      const existingTime = endDate?.format('HH:mm') || '00:00';
                      const newEnd = dayjs(newDateStr + ' ' + existingTime);
                      // Validate: end >= start
                      if (startDate && newEnd.isBefore(startDate)) {
                        return;
                      }
                      setEndDate(newEnd);
                    }}
                  />
                  {!isAllDay && (
                    <StyledTimeInput
                      type="time"
                      step="900"
                      value={endDate?.format('HH:mm') || ''}
                      onChange={(e) => {
                        const newTime = e.target.value;
                        if (!newTime || !endDate) return;
                        const existingDate = endDate.format('YYYY-MM-DD');
                        const newEnd = dayjs(existingDate + ' ' + newTime);
                        // Validate: end >= start
                        if (startDate && newEnd.isBefore(startDate)) {
                          return;
                        }
                        setEndDate(newEnd);
                      }}
                    />
                  )}
                </DateTimeFieldGroup>
              </DateTimeColumn>
            </DateTimeRow>

            <FormControl>
              <StyledCheckbox
                checked={isAllDay}
                onChange={(e) => setIsAllDay(e.target.checked)}
              >
                All Day Event
              </StyledCheckbox>
            </FormControl>

            <FormControl>
              <SectionTitle>Event Type</SectionTitle>
              <StyledRadioButton
                options={eventTypeOptions}
                selected={eventType}
                onSelect={(value) => setEventType(value as EventType)}
                $eventType={eventType}
                $selectedValue={eventType}
              />
            </FormControl>

            <FormControl>
              <TextInput
                label="Short description (displayed in calendar view)"
                placeholder="Enter short description for calendar view..."
                value={description}
                disableForceUppercase={true}
                onChange={handleDescriptionChange}
              />
              <FormControl
                style={{
                  fontSize: '12px',
                  opacity: 0.6,
                  marginTop: '4px',
                  textAlign: 'right',
                  color: description.length > 120 ? '#ef5350' : 'white',
                }}
              >
                {description.length}/120
              </FormControl>
            </FormControl>

            {eventType === 'training' && (
              <>
                <FormControl>
                  <FieldLabel style={{ marginBottom: '30px' }}>
                    Training Duration
                  </FieldLabel>
                  <DurationContainer>
                    <DurationInput
                      min={0}
                      max={1440}
                      value={trainingDuration}
                      onChange={(value) => {
                        setTrainingDuration(Number(value) || 0);
                        setDurationManuallySet(true);
                      }}
                      placeholder="0"
                    />
                    <DurationLabel>min</DurationLabel>
                  </DurationContainer>
                </FormControl>

                <FormControl>
                  <FieldLabel>Training Type</FieldLabel>
                  <AddPlayerContainer>
                    <PlayerInputWrapper>
                      <StyledAutoComplete
                        options={mergedTrainingTypeOptions}
                        value={trainingType}
                        onChange={(value) => setTrainingType(String(value))}
                        onSelect={(value) => {
                          const v = String(value).trim();
                          if (v && !trainingTypes.includes(v)) {
                            setTrainingTypes([...trainingTypes, v]);
                          }
                          setTrainingType('');
                        }}
                        placeholder="Select or type training type..."
                        filterOption={(inputValue, option) =>
                          option!.value
                            .toUpperCase()
                            .indexOf(inputValue.toUpperCase()) !== -1
                        }
                      />
                    </PlayerInputWrapper>
                    <AppButton
                      style={{ margin: 0, height: '56px', fontSize: '16px' }}
                      large={true}
                      onClick={handleAddTrainingType}
                    >
                      Add
                    </AppButton>
                  </AddPlayerContainer>

                  {trainingTypes.length > 0 && (
                    <TrainingTypeList>
                      {trainingTypes.map((type) => (
                        <TrainingTypeItem key={type}>
                          <TrainingTypeName>{type}</TrainingTypeName>
                          <TrainingTypeRemoveButton
                            onClick={() => handleRemoveTrainingType(type)}
                          >
                            Remove
                          </TrainingTypeRemoveButton>
                        </TrainingTypeItem>
                      ))}
                    </TrainingTypeList>
                  )}
                </FormControl>
              </>
            )}

            {eventType === 'match' && (
              <>
                <FormControl>
                  <FieldLabel>Competition Type</FieldLabel>
                  <StyledAutoComplete
                    allowClear={{
                      clearIcon: (
                        <XMarkIcon
                          style={{
                            color: '#fff',
                            height: '30px',
                            width: '30px',
                          }}
                          fontSize={30}
                        />
                      ),
                    }}
                    onClear={() => setCompetitionType('')}
                    options={mergedCompetitionTypeOptions}
                    value={competitionType}
                    onChange={(value) => setCompetitionType(String(value))}
                    placeholder="Select or type competition type..."
                    filterOption={(inputValue, option) =>
                      option!.value
                        .toUpperCase()
                        .indexOf(inputValue.toUpperCase()) !== -1
                    }
                  />
                </FormControl>

                <FormControl>
                  <FieldLabel>Match Plan</FieldLabel>
                  <TextArea
                    value={matchPlan}
                    onChange={(e) => setMatchPlan(e.target.value)}
                    placeholder="Enter match plan details..."
                  />
                </FormControl>

                <FormControl>
                  <FieldLabel>Players</FieldLabel>
                  <AddPlayerContainer className={'add-players-ctr'}>
                    <PlayerInputWrapper>
                      <StyledAutoComplete
                        allowClear={{
                          clearIcon: <XMarkIcon />,
                        }}
                        onClear={() => {
                          setNewPlayerData(null);
                          setNewPlayerName('');
                          setNewPlayerNumber(null);
                        }}
                        options={playerNameOptions}
                        value={newPlayerName}
                        onChange={(value) => {
                          setNewPlayerData(null);
                          setNewPlayerName(String(value));
                        }}
                        onSelect={(value, option) =>
                          handlePlayerNameSelect(String(value), option)
                        }
                        placeholder="Player name..."
                        filterOption={(inputValue, option) =>
                          option!.value
                            .toUpperCase()
                            .indexOf(inputValue.toUpperCase()) !== -1
                        }
                      />
                    </PlayerInputWrapper>
                    <PlayerNumberInput
                      min={1}
                      max={14}
                      value={newPlayerNumber}
                      onChange={(value) => setNewPlayerNumber(Number(value))}
                      placeholder="#"
                    />
                    <AppButton
                      style={{ margin: 0, height: '56px', fontSize: '16px' }}
                      large={true}
                      onClick={handleAddPlayer}
                    >
                      Add Player
                    </AppButton>
                  </AddPlayerContainer>

                  {selectedPlayers.length > 0 && (
                    <PlayerList>
                      {selectedPlayers.map((player) => (
                        <PlayerItem key={player.id}>
                          <PlayerInfo>
                            <PlayerNumber>{player.number}</PlayerNumber>
                            <PlayerName>
                              <span>{player.name}</span>
                              {player.flag && (
                                <PlayerFlagLabel>{player.flag}</PlayerFlagLabel>
                              )}
                            </PlayerName>
                          </PlayerInfo>
                          <PlayerActions>
                            <RoleToggleButton
                              $role={player.role ?? 'str'}
                              onClick={() => handleTogglePlayerRole(player.id)}
                            >
                              {player.role === 'sub' ? 'SUB' : 'STR'}
                            </RoleToggleButton>
                            <RemoveButton
                              onClick={() => handleRemovePlayer(player.id)}
                            >
                              Remove
                            </RemoveButton>
                          </PlayerActions>
                        </PlayerItem>
                      ))}
                    </PlayerList>
                  )}
                </FormControl>
              </>
            )}

            {(eventType === 'match' || eventType === 'training') && (
              <PlaysSection>
                <PlaysSectionHeader>
                  <SectionTitle style={{ margin: 0 }}>Plays</SectionTitle>
                  <AppButton
                    style={{ margin: 0, height: '40px', fontSize: '14px' }}
                    onClick={() => setIsPlayBrowserOpen(true)}
                  >
                    Add
                  </AppButton>
                </PlaysSectionHeader>

                {plays.length === 0 ? (
                  <PlaysEmptyState>No plays added</PlaysEmptyState>
                ) : (
                  <PlaysGrid>
                    {plays.map((play) => (
                      <PlayCard
                        key={play.id}
                        onClick={() => setPlayToPreview(play)}
                      >
                        <PlayCardRemoveButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlays(plays.filter((p) => p.id !== play.id));
                          }}
                        >
                          <XMarkIcon
                            style={{ width: '14px', height: '14px' }}
                          />
                        </PlayCardRemoveButton>
                        <PoolElement
                          snapshot={play.snapshots[0]}
                          halfView
                          poolMaxHeight={100}
                          poolMaxWidth={130}
                          isDrawingEnabled={false}
                          shouldCleanDrawings={false}
                          onDrawingsCleaned={() => {}}
                          shouldUndoLastDrawing={false}
                          onLastDrawingUndone={() => {}}
                          opponentIncrease={0}
                          isStatic
                        />
                        <PlayCardName>{play.name}</PlayCardName>
                      </PlayCard>
                    ))}
                  </PlaysGrid>
                )}
              </PlaysSection>
            )}

            <FormControl style={{ paddingBottom: '100px' }}>
              <FieldLabel>Notes</FieldLabel>
              <TextArea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter general notes..."
              />
            </FormControl>
          </FormContainer>
        </ModalContent>

        <ModalFooter>
          {selectedEvent && selectedEvent.id && (
            <DeleteButton onClick={handleDelete}>Delete Event</DeleteButton>
          )}
          <div style={{ flex: 1 }}></div>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <CancelButton onClick={handleSaveAndView}>Save & View</CancelButton>
          <SaveButton onClick={handleSave}>Save Event</SaveButton>
        </ModalFooter>
      </ModalContainer>

      {/* PlayBrowser Modal (PIP-3) */}
      <PlayBrowserModalOverlay $isOpen={isPlayBrowserOpen}>
        <PlayBrowserModalHeader>
          <PlayBrowserModalTitle>Select a Play</PlayBrowserModalTitle>
          <CloseButton onClick={() => setIsPlayBrowserOpen(false)}>
            <div>
              <XMarkIcon
                style={{
                  color: '#fff',
                  height: '30px',
                  width: '30px',
                }}
                fontSize={30}
              />
            </div>
          </CloseButton>
        </PlayBrowserModalHeader>
        <PlayBrowserModalContent>
          <PlayBrowser
            isOpen={isPlayBrowserOpen}
            onlySelect={true}
            isPro={true}
            colorMode="dark"
            poolMaxHeight={200}
            plays={calculatedPlays}
            onSelectPlay={(formationId) => {
              // Find the play from all available plays
              const allPlays = [...playsStore, ...positions];
              const selectedPlay = allPlays.find((p) => p.id === formationId);
              if (selectedPlay) {
                // Duplicate check by id
                const alreadyAdded = plays.some(
                  (p) => p.id === selectedPlay.id,
                );
                if (!alreadyAdded) {
                  setPlays([...plays, selectedPlay]);
                }
              }
              setIsPlayBrowserOpen(false);
            }}
          />
        </PlayBrowserModalContent>
      </PlayBrowserModalOverlay>

      {/* PlayPlayer (PIP-4) */}
      {playToPreview && (
        <PlayPlayer
          play={playToPreview}
          isOpen={!!playToPreview}
          onClose={() => setPlayToPreview(null)}
        />
      )}
    </ModalOverlay>
  );
};
